import { scrubText } from './telemetry-text';
import type { TelemetryProps } from './telemetry.types';

/** Lo que cuenta como «hacer clic en algo». Un clic en el fondo de una tarjeta no es una acción. */
const INTERACTIVE = [
    'button', 'a', 'summary', '[data-track]',
    '[role="button"]', '[role="link"]', '[role="tab"]', '[role="menuitem"]',
    '[role="option"]', '[role="checkbox"]', '[role="radio"]', '[role="switch"]',
    'input[type="checkbox"]', 'input[type="radio"]', 'input[type="submit"]', 'input[type="button"]',
].join(',');

/**
 * Elementos cuyo texto visible SÍ es un rótulo de la interfaz. Una opción de un desplegable o una
 * casilla pueden decir el nombre de un cliente o de una persona, así que de esas solo se guarda el
 * rol, salvo que lleven `data-track`, `data-testid` o `aria-label`.
 */
const TEXT_IS_LABEL = new Set(['button', 'a', 'summary', 'input:submit', 'input:button', 'role:button', 'role:link', 'role:tab', 'role:menuitem']);

const EMPTY_LABEL = new Set(['null', 'undefined', 'nan', '[object object]']);

export interface ClickDescription {
    name: string;
    props: TelemetryProps;
}

/**
 * Nombra el elemento sobre el que se hizo clic, en este orden: `data-track` > `data-testid` >
 * `aria-label` > `title` > su texto visible (solo si es un rótulo) > su etiqueta.
 * `data-track-ignore` deja fuera un elemento y lo que lleve dentro; `data-track-mask` conserva el
 * clic pero nunca su texto.
 */
export function describeClick (target: EventTarget | null): ClickDescription | null {
    if (!(target instanceof Element)) return null;

    const el = target.closest(INTERACTIVE);
    if (!el || el.closest('[data-track-ignore]')) return null;

    const tag  = el.tagName.toLowerCase();
    const role = el.getAttribute('role') ?? undefined;
    const kind = role ? `role:${role}` : tag === 'input' ? `input:${(el as HTMLInputElement).type}` : tag;
    const masked = !!el.closest('[data-track-mask]');

    const candidates: [string, string | null | undefined][] = [
        ['track',  el.getAttribute('data-track')],
        ['testid', el.getAttribute('data-testid')],
        ['aria',   masked ? null : el.getAttribute('aria-label')],
        ['title',  masked ? null : el.getAttribute('title')],
        ['text',   masked || !TEXT_IS_LABEL.has(kind) ? null : labelText(el)],
    ];

    let source = 'element';
    let name = role ? `${tag}[${role}]` : tag;
    for (const [from, value] of candidates) {
        const clean = scrubText(value ?? '', 60);
        // Un `[attr.aria-label]` enlazado a algo vacío pinta la palabra «null»: eso no es un rótulo.
        if (clean && !EMPTY_LABEL.has(clean.toLowerCase())) { source = from; name = clean; break; }
    }

    const props: TelemetryProps = { tag, source };
    if (role) props.role = role;
    if (el.getAttribute('aria-disabled') === 'true' || (el as HTMLButtonElement).disabled) props.disabled = true;

    const href = tag === 'a' ? internalRoute(el.getAttribute('href')) : undefined;
    if (href) props.href = href;

    return { name, props };
}

function labelText (el: Element): string {
    if (el instanceof HTMLInputElement) return el.value;
    return el.textContent ?? '';
}

/** Solo la ruta interna, sin consulta: un enlace externo o con `?token=` no se guarda. */
function internalRoute (href: string | null): string | undefined {
    if (!href || !href.startsWith('#/')) return undefined;
    return href.slice(1).split(/[?#]/)[0] || undefined;
}
